import { AppVersionService } from '@/http-services/app-version.service';
import { Client, setUser } from '@sentry/core';
import {
  browserProfilingIntegration,
  browserTracingIntegration,
  httpClientIntegration,
  init,
  replayIntegration,
  vueIntegration,
} from '@sentry/vue';
import { App } from 'vue';
import { Router } from 'vue-router';

class SentryUtil {
  private appVersionService = new AppVersionService();
  private _client: Client | undefined;
  private errorBlacklist: (string | RegExp)[] = [
    /^Request failed with status code 401$/,
    /CanceledError/,
    /canceled/i,
    /Bluetooth LE not initialized/,
    /No credentials found/,
    /The operation couldn\'t be completed/,
    /Not connected to device/,
    /Disconnection timeout/,
  ];

  get client(): Client {
    if (!this._client) {
      throw new Error('Sentry client not initialized');
    }
    return this._client;
  }

  setUserContext(username?: string, email?: string) {
    setUser({ id: email, email, username });
  }

  clearUserContext() {
    setUser(null);
  }

  async init(app: App, router: Router) {
    const { profilesSampleRate, replaysOnErrorSampleRate, replaysSessionSampleRate, tracesSampleRate, ignoreErrors } =
      await this.appVersionService.getSentryConfig();

    console.log(
      `[SENTRY] Loading config.\nProfiles sample rate: ${profilesSampleRate};\nReplays on error sample rate: ${replaysOnErrorSampleRate};\nReplays session sample rate: ${replaysSessionSampleRate};\nTraces sample rate: ${tracesSampleRate}`
    );

    this._client = init({
      app,
      dsn: import.meta.env.VITE_SENTRY_DSN,
      integrations: [
        browserTracingIntegration({ router }),
        httpClientIntegration(),
        browserProfilingIntegration(),
        vueIntegration({
          tracingOptions: {
            trackComponents: true,
          },
        }),
        replayIntegration({
          maskAllText: false,
          maskAllInputs: false,
          networkDetailAllowUrls: [
            'localhost',
            'nextcenturymeters.com',
            'api.nextcenturymeters.com',
            'mobile-api.nextcenturymeters.com',
            'mobile-staging-api.nextcenturymeters.com',
          ],
        }),
      ],
      ignoreErrors: this.errorBlacklist,
      sendDefaultPii: true,
      dist: __APP_VERSION__.split('.')[2] ?? '1',
      environment: import.meta.env.DEV ? 'development' : 'production',
      release: `ncss-mobile@${__APP_VERSION__}`,
      tracesSampleRate,
      profilesSampleRate,
      replaysSessionSampleRate: import.meta.env.DEV ? 0 : replaysSessionSampleRate,
      replaysOnErrorSampleRate: import.meta.env.DEV ? 0 : replaysOnErrorSampleRate,
      tracePropagationTargets: [
        'localhost',
        /^https:\/\/*\.nextcenturymeters\.com\//,
        /https\:\/\/mobile-api.nextcenturymeters.com/,
      ],
    });
  }
}

export default new SentryUtil();
